import { lazy, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { FormatTypeEnum } from 'modules/I18N';
import { getBackupCurrentLineAllocation, getIsProgrammaticLineLevelChange } from 'store/dealManagement/selectors';
import { handleValueFormat } from 'components/pages/DealWithLines/common/RightSidebar/utils';
import { DealStatus, DealType } from 'components/common/types/Deal.types';
import { Store } from 'components/common/types/Store.types';
import { getDealType } from 'store/dealManagement/selectors/getDealType';
import { Asset } from 'components/common/types/Asset.types';
import {
  getCurrentLineAvailability,
  getSelectedDistributionAssets,
} from 'store/dealManagement/selectors/availabilityAssets';
import Preloader from 'lib/Preloader';
import { ProposalCard } from 'components/common/Deal/Cards/ProposalCard/ProposalCard';
import { DealSummaryCard } from '../DealSummary/DealSummaryCard/DealSummaryCard';
import DealSummaryCardSection from '../DealSummary/DealSummaryCardSection';
import { DealSummaryCardSectionLineType } from '../DealSummary/DealSummaryCardSection/DealSummaryCardSection.types';
import CheckAllocationButton from '../DealSummary/CheckAllocationButton';
import { AvailabilityCard } from '../Cards/AvailabilityCard/AvailabilityCard';
import AllocationCard from '../Cards/AllocationCard';
import { getDealSummaryCardVisibility, getLineSummaryCardVisibility } from '../Cards/selectors';

const DeliveryCard = lazy(() => import('../Cards/DeliveryCard/DeliveryCard'));

export const DealSummaryLineInfo: React.FC = () => {
  const { currencyCode, localeCode } = useSelector((state: RootState) => state.publisher.configuration);
  const backupCurrentLineAllocation = useSelector(getBackupCurrentLineAllocation);
  const dealType = useSelector(getDealType);
  const bookingStatusCode = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);
  const availability = useSelector(getCurrentLineAvailability);
  const impressions = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.impressions);
  const budget = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.budget);
  const frames = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.frames);
  const currentLineAssets = useSelector(getSelectedDistributionAssets);
  const deliveredImpressions = useSelector(
    (state: Store) => state.dealManagement.commonDeal.currentLine.deliveredImpressions,
  );
  const currentLineImpressionMetrics = useSelector(
    (state: Store) => state.dealManagement.commonDeal.currentLine.impressionMetrics,
  );
  const remainingImpressions = useSelector(
    (state: Store) => state.dealManagement.commonDeal.currentLine.currentRemainingImpressions,
  );

  const isGuaranteed = dealType === DealType.GUARANTEED;
  const { isCheckAllocationButtonVisible } = useSelector(getDealSummaryCardVisibility);
  const isLineLevelChange = useSelector(getIsProgrammaticLineLevelChange);
  const { isLineProposalCardVisible, isLineAvailabilityCardVisible, isLineDeliveryCardVisible } =
    useSelector(getLineSummaryCardVisibility);
  const showGoalCard = isGuaranteed;

  const isReallocateButtonVisible = currentLineImpressionMetrics && currentLineImpressionMetrics?.osi < 100;

  return (
    <div className="flex flex-col gap-3 mb-[12px]" data-test-id="deal-summary-line-info">
      <AllocationCard
        allocatedFrames={backupCurrentLineAllocation.allocatedFrames}
        allocatedImpressions={backupCurrentLineAllocation.allocatedImpressions}
        assets={backupCurrentLineAllocation.assets as Asset[]}
        totalCost={backupCurrentLineAllocation.totalCost}
      />
      <ProposalCard
        allocatedFrames={availability.allocatedFrames}
        allocatedImpressions={availability.allocatedImpressions}
        assets={availability.assets as Asset[]}
        availableFrames={availability.availableFrames}
        availableImpressions={availability.availableImpressions}
        totalCost={availability.totalCost}
        isAvailabilityHidden={bookingStatusCode === DealStatus.PENDING_APPROVAL && !isLineLevelChange}
        isVisible={isLineProposalCardVisible}
      />
      <AvailabilityCard
        assets={currentLineAssets as Asset[]}
        availableImpressions={availability.availableImpressions}
        availableFrames={availability.availableFrames}
        totalCost={availability.totalCost}
        isVisible={isLineAvailabilityCardVisible}
      />
      {isCheckAllocationButtonVisible && !isReallocateButtonVisible ? <CheckAllocationButton /> : null}
      <Suspense
        fallback={
          <div className="relative">
            <Preloader />
          </div>
        }
      >
        {isLineDeliveryCardVisible && (
          <DeliveryCard
            expectedImpressionsElapsed={currentLineImpressionMetrics?.expectedImpressionsElapsed}
            totalAllocatedImpressions={currentLineImpressionMetrics?.totalAllocatedImpressions}
            soldImpressions={
              currentLineImpressionMetrics?.totalAllocatedImpressions
                ? currentLineImpressionMetrics.soldImpressions
                : deliveredImpressions
            }
            remainingImpressions={
              currentLineImpressionMetrics?.totalAllocatedImpressions
                ? currentLineImpressionMetrics.remainingImpressions
                : remainingImpressions
            }
            osi={currentLineImpressionMetrics?.osi}
            isReallocateButtonVisible={isReallocateButtonVisible}
          />
        )}
      </Suspense>

      <DealSummaryCard header="GOAL" isVisible={showGoalCard}>
        <DealSummaryCardSection
          id="budget-or-impressions"
          lines={[
            {
              label: budget ? 'Budget' : 'Impressions',
              value:
                (!!budget &&
                  handleValueFormat({
                    valueType: FormatTypeEnum.ROUNDED_NUMBER,
                    value: budget,
                    currencyCode,
                    localeCode,
                  })) ||
                (!!impressions &&
                  handleValueFormat({
                    valueType: FormatTypeEnum.ROUNDED_NUMBER,
                    value: impressions,
                    currencyCode,
                    localeCode,
                  })) ||
                '--',
              lineType: DealSummaryCardSectionLineType.SUBHEADER,
            },
          ]}
        />
        <DealSummaryCardSection
          id="frames"
          lines={[
            {
              label: 'Frames',
              value:
                ((!!impressions || !!budget) &&
                  !!frames &&
                  handleValueFormat({
                    valueType: FormatTypeEnum.ROUNDED_NUMBER,
                    value: frames,
                    currencyCode,
                    localeCode,
                  })) ||
                '--',
              lineType: DealSummaryCardSectionLineType.SUBHEADER,
            },
          ]}
        />
      </DealSummaryCard>
    </div>
  );
};
